/**
 * Kleiner 3D-Viewer fuer die Shop-Seiten.
 *
 * Zeigt ein GLB drehend an, ohne Bedienelemente — es geht um Verdeutlichung des
 * Schnitts, nicht um einen Konfigurator. Der begehbare Raum bleibt Sub Rosa.
 *
 * Verwendung im Liquid:
 *   <div class="merch3d" data-glb="{{ 'datei.glb' | asset_url }}" data-height="2.0"
 *        data-start="150" data-speed="0.30"></div>
 *
 * data-start ist der Startwinkel in Grad (0 = Vorderseite, 180 = Rueckendruck),
 * data-speed das Drehtempo in rad/s.
 *
 * Warum eigener Viewer und nicht <model-viewer>: Die Shop-Seiten laden heute ein
 * einziges Skript. Three.js kommt hier ohnehin aus derselben Quelle wie in
 * subrosa.js, ein zweites Paket waere zusaetzliches Gewicht fuer dieselbe Sache.
 *
 * Drei Sparmassnahmen, weil das auf Verkaufsseiten laeuft:
 *   - geladen wird erst, wenn der Rahmen in Sichtweite kommt (IntersectionObserver)
 *   - gerendert wird nur, solange er sichtbar ist — sonst steht die Schleife
 *   - ein Renderer pro Rahmen, aber hoechstens MAX_VIEWER; darueber bleibt das
 *     Standbild stehen. Browser deckeln die Zahl gleichzeitiger WebGL-Kontexte.
 */
import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/addons/loaders/DRACOLoader.js';

const MAX_VIEWER = 4;
let aktive = 0;

const draco = new DRACOLoader();
draco.setDecoderPath('https://www.gstatic.com/draco/versioned/decoders/1.5.7/');
const gltfLoader = new GLTFLoader();
gltfLoader.setDRACOLoader(draco);

function baueViewer(host) {
  if (aktive >= MAX_VIEWER) return;
  aktive += 1;

  const url = host.dataset.glb;
  if (!url) return;
  const zielHoehe = parseFloat(host.dataset.height || '2.0');
  const drehTempo = parseFloat(host.dataset.speed || '0.42');
  // Startwinkel in Grad. Bei 0 zeigen die Modelle nach vorn, bei 180 den
  // Ruecken mit dem Druck (nachgemessen an vier Ansichten). Stehen mehrere
  // Stuecke nebeneinander, sollten sie verschiedene Startwinkel und Tempi
  // bekommen — sonst stehen alle zur selben Zeit seitlich und man sieht eine
  // Weile lang gar keinen Druck.
  const startWinkel = parseFloat(host.dataset.start || '0') * Math.PI / 180;

  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(38, 1, 0.1, 100);

  const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.6));
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.25;
  host.appendChild(renderer.domElement);

  // Ausgeleuchtet fuer HELLEN Grund — die Shop-Seiten liegen auf 245,245,240.
  // In Sub Rosa hebt ein pinker Rim die schwarzen Stuecke aus dem Dunkeln; hier
  // ist es umgekehrt: Vor Hellgrau steht Schwarz ohnehin als Silhouette, und ein
  // kraeftiger Rim faerbt nur die Kanten ein. Stattdessen viel weiches Fuelllicht,
  // damit im Stoff Zeichnung bleibt statt einer schwarzen Flaeche.
  scene.add(new THREE.AmbientLight(0xffffff, 2.1));
  scene.add(new THREE.HemisphereLight(0xffffff, 0xb9b9ae, 1.6));
  const key = new THREE.DirectionalLight(0xfff6ec, 2.2);
  key.position.set(2.2, 3.0, 2.6);
  scene.add(key);
  const fill = new THREE.DirectionalLight(0xeef2ff, 1.5);
  fill.position.set(-2.8, 0.4, 2.0);
  scene.add(fill);
  const rim = new THREE.DirectionalLight(0xFF3E8E, 0.55);
  rim.position.set(-1.4, 1.4, -2.6);
  scene.add(rim);

  const gruppe = new THREE.Group();
  gruppe.rotation.y = startWinkel;
  scene.add(gruppe);

  let geladen = false;
  // Breite des Stuecks nach Skalierung. Kleidung ist durch die Aermel breiter
  // als hoch — passt man die Kamera nur nach Hoehe an, laeuft sie seitlich aus
  // dem Rahmen. Beim Drehen wandert die Aermelspanne ausserdem in die Tiefe,
  // deshalb zaehlt der groessere der beiden Grundriss-Werte.
  let modellRadius = zielHoehe * 0.5;

  gltfLoader.load(url, (gltf) => {
    const model = gltf.scene;
    const bbox = new THREE.Box3().setFromObject(model);
    const groesse = bbox.getSize(new THREE.Vector3());
    const mitte = bbox.getCenter(new THREE.Vector3());
    const faktor = zielHoehe / groesse.y;
    model.scale.setScalar(faktor);
    model.position.set(-mitte.x * faktor, -mitte.y * faktor, -mitte.z * faktor);
    model.traverse((n) => { if (n.isMesh) { n.castShadow = false; n.receiveShadow = false; } });
    gruppe.add(model);
    modellRadius = Math.max(groesse.x, groesse.z) * faktor * 0.5;
    geladen = true;
    groesseSetzen();
    host.classList.add('is-ready');
  }, undefined, () => host.classList.add('is-failed'));

  function groesseSetzen() {
    const b = host.clientWidth || 1;
    const h = host.clientHeight || 1;
    renderer.setSize(b, h, false);
    camera.aspect = b / h;
    // Abstand so waehlen, dass das Stueck in JEDER Drehlage vollstaendig im
    // Rahmen bleibt: einmal nach Hoehe, einmal nach Breite gerechnet, der
    // groessere Wert gewinnt. Sonst schneidet ein Hochformat die Aermel ab.
    const fovRad = (camera.fov * Math.PI) / 180;
    const halbeHoehe = zielHoehe * 0.5;
    const nachHoehe = halbeHoehe / Math.tan(fovRad / 2);
    const nachBreite = modellRadius / (Math.tan(fovRad / 2) * camera.aspect);
    camera.position.set(0, 0, Math.max(nachHoehe, nachBreite) * 1.18);
    camera.lookAt(0, 0, 0);
    camera.updateProjectionMatrix();
  }
  groesseSetzen();
  new ResizeObserver(groesseSetzen).observe(host);

  let sichtbar = false;
  let laeuft = false;
  let letzte = performance.now();

  function schleife(jetzt) {
    if (!sichtbar) { laeuft = false; return; }
    requestAnimationFrame(schleife);
    const dt = Math.min((jetzt - letzte) / 1000, 0.1);
    letzte = jetzt;
    if (geladen) gruppe.rotation.y += drehTempo * dt;
    renderer.render(scene, camera);
  }

  const reduziert = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  new IntersectionObserver((eintraege) => {
    eintraege.forEach((e) => {
      sichtbar = e.isIntersecting && !reduziert;
      if (sichtbar && !laeuft) { laeuft = true; letzte = performance.now(); requestAnimationFrame(schleife); }
      if (!sichtbar && reduziert && geladen) renderer.render(scene, camera);
    });
  }, { rootMargin: '120px' }).observe(host);
}

// Erst laden, wenn der Rahmen in die Naehe des Bildschirms kommt.
const vorbereiten = new IntersectionObserver((eintraege, beobachter) => {
  eintraege.forEach((e) => {
    if (!e.isIntersecting) return;
    beobachter.unobserve(e.target);
    baueViewer(e.target);
  });
}, { rootMargin: '300px' });

document.querySelectorAll('.merch3d[data-glb]').forEach((el) => vorbereiten.observe(el));
